import { useCallback, useEffect, useMemo } from "react";
import { Card, CardHeader } from "reactstrap";
import {
	TableContext,
	useTableContext,
} from "../../../../../components/Table/context";
import { useAccess } from "../../../../../utils/hooks/access";
import Table from "../../../components/TimeSheetTable";
import AddTimesheetNote from "../../../components/TimeSheetTable/AddTimesheetNote";
import CopyTimesheetModal from "../../../Home/Actions/CopyTimesheetModal";
import History from "../../../Home/Actions/HistoryDrawer";
import Actions from "./Actions";

import { useDateFilters } from "../../../hooks/useDateFilters";
import { useFetchUserTimeSheets } from "../../hooks/useFetchUserTimeSheets";

import { useDateIdMap } from "../../../components/TimeSheetTable/hooks/useDateIdMap";
import { useTimeSheetTable } from "../../../components/TimeSheetTable/hooks/useTable";

import { useBatchUpdate } from "./hooks/useBatchUpdate";
import { useDateActions } from "./hooks/useDateActions";
import { useEdit } from "./hooks/useEdit";
import { useRemoveActions } from "./hooks/useRemoveActions";

import { useVisible } from "../../../../../utils/hooks/useVisible";
import { useDateTimesheetMap } from "../../../components/TimeSheetTable/hooks/useDateTimesheetMap";
import { useColumns } from "../../../components/TimeSheetTable/useColumns";
import { useFilters } from "../../../filtersContext";

import moment from "moment-timezone";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import useSWR from "swr";
import { useColumnsPreferences } from "../../../../Settings/Configuration/hooks/useColumnsPreferences";
import { useCompanyPreferences } from "../../../../Settings/Configuration/hooks/usePreferences";
import { useColumnsVisibilityPreferences } from "../../../../Settings/Configuration/hooks/useColumnsVisibilityPreferences";
import { useWaiveTimeSheet } from "../../../api/useWaiveTimeSheet";
import { usePayGroupItems } from "../../../components/TimeSheetTable/hooks/usePayGroupItems";
import { useSortTimeSheets } from "../../../components/TimeSheetTable/hooks/useSortTimeSheets";
import { useInOutDateModal } from "./hooks/useInOutDateModal";
import { useOverrides } from "../../../api/useOverrides";
import { usePayCodeRequestModal } from "../../hooks/usePayCodeRequestModal";
import { useSyncTimesheetModal } from "../../../components/TimeSheetTable/SyncTimesheetModal/useSyncTimesheetModal";
import { useRetroAdjustments } from "../../../components/TimeSheetTable/hooks/useRetroAdjustment";
import { useByPayDateRetro } from "../../../components/TimeSheetTable/hooks/useByPayDateRetro";
import { useManadatoryBreakWarning } from "../../../components/MandatoryBreakWarning/useMandatoryBreakWarning";

function TimeSheetTable({ user, state, openDaySummary, openAllSummary, openOvertimeDrawer, openRetroModal, openDetails, isMyTimesheet = false }) {
	const { t } = useTranslation();
	const hideDailySummary = useCompanyPreferences("timeSheetTeam.timeSheet.hideDailySummary");
	const hideWeeklySummary = useCompanyPreferences("timeSheetTeam.hideWeeklySummary");
	const hideClockDatePicker = useCompanyPreferences("timeSheetTeam.timeSheet.hideClockDatePicker");

	const [filters] = useFilters()
	const { from, to } = useDateFilters();
	const {
		state: parentState,
		setRowState,
		updateUser,
		refetchOverview,
	} = useTableContext();
	const { data: baseData, loading } = state;

	const data = useSortTimeSheets(baseData);

	const setState = useMemo(() => {
		return setRowState.bind(null, user.id);
	}, [user, setRowState]);

	const {
		mutate: refetchTimeSheets,
		scheduledHours,
		mutateScheduledHours,
		clockExceptions,
		mutateClockExceptions
	} = useFetchUserTimeSheets({
		params: useMemo(() => ({ from, to, filters }), [from, to, filters]),
		user: user.id,
		setState
	});

	const {
		data: timeOffPlans,
		mutate: mutateTimeOffPlans,
	} = useSWR(() => {
		if (!user?.id || !from || !to) {
			return null;
		}
		const startDate = moment(from).format('YYYY-MM-DD');
		const endDate = moment(to).format('YYYY-MM-DD');
		return {
			url: `/users/${user.id}/time-off`,
			params: {
				range: {
					startDate,
					endDate,
				},
				bringAllPlans: true,
			},
		};
	});

	const {
		data: overrides,
		mutate: mutateOverrides
	} = useOverrides(user.id, from, to);

	const actionsRefetch = useCallback(
		(value) => {
			if (value?.result?.[0]) {
				const { result } = value;
				updateUser(result[0]);
			}
			return Promise.all([
				refetchOverview(),
				refetchTimeSheets(),
				mutateScheduledHours(),
				mutateClockExceptions(),
				mutateTimeOffPlans(),
				mutateOverrides(),
			]);
		},
		[updateUser, refetchOverview, refetchTimeSheets, mutateScheduledHours, mutateClockExceptions, mutateTimeOffPlans, mutateOverrides],
	);

	const { approve, unapprove, lock, unlock } = useDateActions(
		actionsRefetch,
		filters,
	);

	const { modal: removeModal, remove } = useRemoveActions({
		refetch: actionsRefetch,
		setState,
		filters,
		userId: user?.id,
		isMyTimesheet
	});

	const {
		visible: editVisible,
		open: openEdit,
		close: closeEdit,
		selected: editSelected,
		loading: editLoading,
		error: editError,
		submit: editSubmit,
	} = useEdit({ refetch: actionsRefetch, filters, isMyTimesheet });

	const viewSummary = useCallback(
		(date) => {
			openDaySummary(user, date);
		},
		[user, openDaySummary],
	);

	const viewAllSummary = useCallback(() => {
		openAllSummary(user)
	}, [openAllSummary, user]);

	const [openPayCodeRequest, payCodeRequestModal] = usePayCodeRequestModal();
	const {
		visible: historyVisible,
		selected: selectedTimesheet,
		open: openHistory,
		close: closeHistory,
	} = useVisible();

	const {
		visible: copyModalVisible,
		selected: selectedInfo,
		open: openCopyModal,
		close: closeCopyModal,
	} = useVisible();
	const { open: openInOutDateModal, modal: inOutDateModal } = useInOutDateModal();
	const [openSyncTimesheetModal, syncTimesheetModal] = useSyncTimesheetModal();

	const { waiveMealPremium: _waive, cancelWaiveMealPremium } = useWaiveTimeSheet();
	const waive = useCallback((date, data) => {
		_waive(user.id, date, data, async () => {
			await Promise.all([
				refetchOverview(),
				refetchTimeSheets(),
				mutateScheduledHours(),
				mutateClockExceptions(),
				mutateOverrides(),
			]);
			toast.success(t("meal-premium-waived-successfully"))
		});
	}, [user.id, refetchOverview, mutateOverrides, refetchTimeSheets, mutateScheduledHours, mutateClockExceptions, _waive, t]);

	const cancelWaive = useCallback((date, data) => {
		cancelWaiveMealPremium(user.id, date, data, async () => {
			await Promise.all([
				refetchOverview(),
				refetchTimeSheets(),
				mutateScheduledHours(),
				mutateClockExceptions(),
			]);
			toast.success(t("meal-premium-waive-cancelled-successfully"))
		});
	}, [user.id, refetchOverview, refetchTimeSheets, mutateScheduledHours, mutateClockExceptions, cancelWaiveMealPremium, t]);

	const { hasAccess: canCreate } = useAccess(isMyTimesheet ? "timeSheet.canCreateMyTimesheet" : "timeSheet.canCreate");
	const { hasAccess: canEdit } = useAccess(isMyTimesheet ? "timeSheet.canEditMyTimesheet" : "timeSheet.canEdit");
	const { hasAccess: canViewOriginalClocks } = useAccess(
		isMyTimesheet ? "timeSheet.canViewMyOriginalClocks" : "timeSheet.canViewOriginalClocks",
	);
	const { hasAccess: canViewHistory } = useAccess(isMyTimesheet ? "timeSheet.canViewHistoryMyTimesheet" : "timeSheet.canViewHistory");
	const { hasAccess: canViewExceptions } = useAccess(isMyTimesheet ? "timeSheet.canViewClockExceptionsMyTimesheet" : "timeSheet.canViewClockExceptions");
	const { hasAccess: canChangeClockDate } = useAccess(isMyTimesheet ? "timeSheet.canChangeClockDateMyTimesheet" : "timeSheet.canChangeClockDate");
	const { hasAccess: canViewRates } = useAccess("canViewRates");
	const syncSequentialTimesheets = useCompanyPreferences("timeSheetGeneral.syncSequentialTimesheets");
	const showMandatoryBreakWarning = useCompanyPreferences("timeSheetGeneral.showMandatoryBreakWarning");
	const { open: openMandatoryBreakWarning, modal: mandatoryBreakWarningModal } = useManadatoryBreakWarning();
	const dateIdMap = useDateIdMap({ data });
	const dateTimesheetMap = useDateTimesheetMap({ data });
	const columns = useColumns({
		user,
		scheduleInfo: scheduledHours,
		overrides,
		clockExceptions,
		canCreate,
		canEdit,
		dateIdMap,
		dateTimesheetMap,
		approve,
		unapprove,
		lock,
		unlock,
		viewSummary: !hideDailySummary && viewSummary,
		edit: editSubmit ? openEdit : null,
		// TODO: the user might not have any of the permissions, but he should be able to check the overrides (or we should add a permission set for the overrides)
		view: (canViewHistory || canViewOriginalClocks || canViewExceptions) ? openHistory : undefined,
		remove,
		copy: openCopyModal,
		openInOutDateModal: (canChangeClockDate && !hideClockDatePicker) ? openInOutDateModal : undefined,
		openSyncTimesheetModal: syncSequentialTimesheets ? openSyncTimesheetModal : undefined,
		waive,
		cancelWaive,
		canViewRates,
		openMandatoryBreakWarning: showMandatoryBreakWarning ? openMandatoryBreakWarning : undefined,
	});
	const { data: retroAdjustments, mutate: mutateRetroAdjustment } = useRetroAdjustments(user);
	const { data: byPayDateRetros, mutate: mutateByPayDateRetro } = useByPayDateRetro(user);
	const batchUpdateRefetch = useCallback((user) => {
		updateUser(user);
		return Promise.all([
			refetchOverview(),
			refetchTimeSheets(),
			mutateScheduledHours(),
			mutateClockExceptions(),
			mutateRetroAdjustment(),
			mutateByPayDateRetro(),
			mutateOverrides(),
		]);
	}, [
		updateUser,
		refetchOverview,
		refetchTimeSheets,
		mutateScheduledHours,
		mutateClockExceptions,
		mutateRetroAdjustment,
		mutateByPayDateRetro,
		mutateOverrides,
	]);

	const { submit: batchUpdateSubmit, loading: batchUpdateLoading } =
		useBatchUpdate({
			user,
			refetch: batchUpdateRefetch,
			filters,
		});

	const payGroupItems = usePayGroupItems(user);

	const table = useTimeSheetTable({
		user,
		openOvertimeDrawer: !isMyTimesheet ? openOvertimeDrawer : undefined,
		columns: useColumnsPreferences(columns, "timeSheetTeam.timeSheet"),
		data,
		dateIdMap,
		batchUpdate: batchUpdateSubmit,
		submitting: batchUpdateLoading,
		payGroupItems,
		type: "timeSheetTeam",
		timeOffPlans,
		retroAdjustments,
	});

	const hiddenColumnsMap = useColumnsVisibilityPreferences("timeSheetTeam.timeSheet");
	useEffect(() => {
		if (hiddenColumnsMap) {
			table.setHiddenColumns(Object.keys(hiddenColumnsMap));
		}
	}, [table, hiddenColumnsMap]);

	useEffect(() => {
		setState((prev) => ({
			...prev,
			hasChanges: table.hasChanges,
		}));
	}, [table.hasChanges, setState]);
	return (
		<TableContext.Provider value={table}>
			<Card className="mb-auto top-0" style={{ zIndex: 3 }} >
				<CardHeader className="border-top p-3">
					<Actions
						user={user}
						filters={filters}
						approve={approve}
						unapprove={unapprove}
						lock={lock}
						unlock={unlock}
						remove={remove}
						viewSummary={!hideWeeklySummary && viewAllSummary}
						retroAdjustments={retroAdjustments}
						byPayDateRetros={byPayDateRetros}
						openRetroModal={openRetroModal}
						payGroupItems={payGroupItems}
						mutateRetroAdjustment={mutateRetroAdjustment}
						openPayCodeRequest={openPayCodeRequest}
						refetchTimeSheets={refetchTimeSheets}
						openDetails={openDetails}
					/>
				</CardHeader>
			</Card>

			<Table loading={loading} />

			{editSubmit && (
				<AddTimesheetNote
					visible={editVisible}
					close={closeEdit}
					onFinish={editSubmit}
					error={editError}
					submitting={editLoading}
					values={editSelected}
				/>
			)}

			{historyVisible && (
				<History
					open={historyVisible}
					close={closeHistory}
					user={selectedTimesheet?.user}
					selected={selectedTimesheet}
					canViewRates={canViewRates}
					overrides={overrides}
					refetch={actionsRefetch}
				/>
			)}

			{copyModalVisible && (
				<CopyTimesheetModal
					user={selectedInfo[0]?.user}
					isOpen={copyModalVisible}
					close={closeCopyModal}
					timesheet={selectedInfo}
					refetch={batchUpdateRefetch}
					filters={{
						filters: filters,
						pageIndex: parentState.pageIndex,
						pageSize: parentState.pageSize,
						sortBy: parentState.sortBy,
					}}
					canViewRates={canViewRates}
				/>
			)}

			{payCodeRequestModal}
			{removeModal}

			{inOutDateModal}
			{syncTimesheetModal}
			{mandatoryBreakWarningModal}
		</TableContext.Provider>
	);
}

export default TimeSheetTable;
